import React, { useState, useMemo, useRef, useEffect } from 'react';
import { usePrompts } from '../context/PromptContext';
import { useToast } from '../context/ToastContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEye, FiRefreshCw, FiPlay, FiCopy, FiCheck, FiAlertCircle, FiChevronDown } = FiIcons;

const PreviewPane = ({ content, placeholders }) => {
  const { variables: globalVariables } = usePrompts();
  const { success: showSuccess, error: showError } = useToast();
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const clearValues = () => {
    setPlaceholderValues({});
  };

  const handlePlaceholderChange = (key, value) => {
    setPlaceholderValues(prev => ({ ...prev, [key]: value }));
  };

  const handleDropdownSelect = (key, value) => {
    handlePlaceholderChange(key, value);
    setOpenDropdown(null);
  };

  const toggleDropdown = (key) => {
    setOpenDropdown(prev => (prev === key ? null : key));
  };

  const previewContent = useMemo(() => {
    let result = content;
    placeholders.forEach(placeholder => {
      const key = placeholder.replace(/[{}]/g, '');
      const value = placeholderValues[key] || placeholder;
      // Escape special regex characters in the placeholder, then replace globally
      const escapedPlaceholder = placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      result = result.replace(new RegExp(escapedPlaceholder, 'g'), value);
    });
    return result;
  }, [content, placeholders, placeholderValues]);

  const handleCopyResult = async () => {
    if (!previewContent) return;
    try {
      await navigator.clipboard.writeText(previewContent);
      setCopied(true);
      setCopyError(null);
      showSuccess('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const errorMsg = err.message || 'Failed to copy to clipboard';
      setCopyError(errorMsg);
      showError(errorMsg);
    }
  };

      return (
     <div className="neo-card p-0 border-2 border-black shadow-neo">
      <div className="neo-window-header bg-neo-green py-2">
        <div className="flex items-center">
          <SafeIcon icon={FiEye} className="mr-2" size={16} />
          <span className="text-sm">Output Preview</span>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={clearValues}
            className="text-[10px] border border-black bg-white px-3 py-2 min-w-[44px] min-h-[44px] hover:bg-gray-100 shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] active:shadow-none flex items-center justify-center"
            title="Reset"
          >
            <SafeIcon icon={FiRefreshCw} size={10} />
          </button>
        </div>
      </div>

      <div className="p-4 bg-white">
        {placeholders.length > 0 && (
          <div className="mb-4 bg-gray-50 border-2 border-black p-3 max-h-[180px] overflow-y-auto overscroll-contain" ref={dropdownRef}>
            <h4 className="text-[10px] font-bold uppercase mb-3 border-b border-black pb-1 text-gray-500">
              Input Variables
            </h4>
            <div className="space-y-3">
              {placeholders.map((placeholder) => {
                const key = placeholder.replace(/[{}]/g, '');
                const storedValues = globalVariables[key];
                const hasOptions = storedValues && storedValues.length > 0;
                
                // Filter options based on current input
                const filteredValues = hasOptions 
                    ? storedValues.filter(val => !placeholderValues[key] || val.toLowerCase().includes(placeholderValues[key].toLowerCase()))
                    : [];

                return (
                  <div key={placeholder} className="relative">
                    <label className="block text-[10px] font-bold text-black mb-1 uppercase">
                      {key}
                    </label>
                    <div className="relative">
                        <input
                        type="text"
                        value={placeholderValues[key] || ''}
                        onChange={(e) => {
                            handlePlaceholderChange(key, e.target.value);
                            if (hasOptions) setOpenDropdown(key);
                        }}
                        onFocus={() => {
                            if (hasOptions) setOpenDropdown(key);
                        }}
                        className="neo-input text-xs input-touch w-full pr-8"
                         placeholder={`Value for ${key}...`}
                         autoComplete="off"
                         />

                        {hasOptions && (
                            <button
                                type="button"
                                onClick={() => toggleDropdown(key)}
                                className="absolute right-1 top-1/2 -translate-y-1/2 p-2 min-w-[44px] min-h-[44px] text-gray-500 hover:text-black flex items-center justify-center"
                            >
                                <SafeIcon icon={FiChevronDown} size={14} />
                            </button>
                        )}
                    </div>

                    {/* Dropdown */}
                    {openDropdown === key && hasOptions && (
                        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border-2 border-black shadow-neo max-h-40 overflow-y-auto">
                            {filteredValues.map((val, idx) => (
                                <button
                                key={idx}
                                onClick={() => handleDropdownSelect(key, val)}
                                className="w-full text-left px-3 py-2 text-xs hover:bg-neo-yellow font-mono border-b border-gray-100 last:border-0"
                                >
                                {val}
                                </button>
                            ))}
                             {filteredValues.length === 0 && (
                                <div className="px-3 py-2 text-gray-400 text-xs italic">No matching values</div>
                            )}
                        </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[10px] font-bold uppercase flex items-center">
              <SafeIcon icon={FiPlay} size={12} className="mr-1" />
              Rendered Output
            </h4>
            <button
                onClick={handleCopyResult}
                className={`flex items-center text-[10px] font-bold uppercase px-3 py-2 min-w-[44px] min-h-[44px] transition-colors ${copyError ? 'bg-red-500 hover:bg-red-600' : 'bg-black hover:bg-gray-800'}`}
                title={copyError ? 'Copy failed' : 'Copy Result'}
            >
                <SafeIcon icon={copyError ? FiAlertCircle : (copied ? FiCheck : FiCopy)} size={14} className="mr-1"/>
                {copyError ? 'Failed' : (copied ? 'Copied' : 'Copy')}
            </button>
          </div>
          <div className="bg-white text-black p-4 font-mono text-sm leading-relaxed whitespace-pre-wrap border-2 border-black min-h-[150px] max-h-[180px] sm:max-h-[250px] md:max-h-[300px] overflow-y-auto overscroll-contain touch-pan-y">
            {previewContent || '> Waiting for input...'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewPane;